import React, { useEffect, useReducer, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import moment from "moment";

import { setEditCurrentView, updateAlarms } from "store/actions/charts";
import { editChartWidget, editTagsView, refreshPlotData } from "store/thunk/charts";

import { getLayout } from "utils/utils";
import { ChartStrings, DROPDOWN_STRINGS } from "constants/constantStrings";

import DatePopover from "components/DatePopover/DatePopover.jsx";
import Dropdown from "components/Dropdown";
import DupontIconButton from "components/DupontIconButton";
import ScreenStatusBox from "components/ScreenStatusBox";

import ChartConstant from "../../../constants/chartConstant";
import ChartUtil from "../../../utils/ChartUtil";

import ChartAddTag from "./ChartAddTag.jsx";
import CHART_HEADER_BUTTONS from "./ChartHeader.buttons";

function chartHeaderReducer(state, data) {
  return { ...state, ...data };
}

function getWarningMessage(tagsCount) {
  const { addTagsDisabledWarningMsg, multiYDisabledWarningMsg } = ChartStrings;
  return tagsCount >= ChartConstant.MAX_TAGS_COUNT
    ? addTagsDisabledWarningMsg
    : tagsCount > ChartConstant.MAX_MULTI_Y_TAGS_COUNT
    ? multiYDisabledWarningMsg
    : null;
}

function getChartHeaderState(chartData, currentLayouts) {
  const { Tags, NormalizedTags, TagsWithoutData, Alarms, IsChartUpdating, TagsToShow } = chartData;
  const tagsCount = Tags?.length || 0;
  const tagsToShowCount = TagsToShow?.length || 0;
  const activeLayout = currentLayouts ? getLayout(currentLayouts) : null;
  const isLayout = Boolean(activeLayout && Object.keys(activeLayout)?.length);
  const newState = {
    isChartPlotTypesDisabled: IsChartUpdating || tagsCount === 0 || tagsCount === TagsWithoutData?.length,
    isMultiYDisabled: tagsToShowCount <= 1 || tagsToShowCount > 5,
    chartWarningMessage: getWarningMessage(tagsToShowCount),
    isMultiplotDisabled: tagsCount <= 1,
    isAddTagsDisabled: !isLayout || IsChartUpdating || tagsCount >= 15,
    isPlotRangeDisabled: !tagsCount || IsChartUpdating,
    isEditTagsVisible: !IsChartUpdating && Boolean(tagsCount),
    isEditAlarmsDisabled: Alarms?.length === 0,
    isEditNormalizeDisabled: NormalizedTags?.length === 0,
    popoverAnchor: null,
    noTagsDataErrorAlert: Boolean(TagsWithoutData?.length) && !IsChartUpdating
  };
  return newState;
}

const ChartHeader = props => {
  const { chartData, layout } = useSelector(state => state);
  const dispatch = useDispatch();
  const { Tags, Format, Duration, DurationRanges, TagsWithoutData, IsChartUpdating } = chartData;
  const { currentLayouts } = layout;
  const divRef = useRef();
  const { CHART_HEADER_ACTIONS, CHART_PLOT_FORMATS, LIVE_CHART } = ChartConstant;
  const { EDIT_TAGS, EDIT_ALARMS, EDIT_NORMALIZE_FILTERS, MULTI_PLOT, MULTI_Y_PLOT } = CHART_HEADER_ACTIONS;

  const [state, chartDispatch] = useReducer(chartHeaderReducer, getChartHeaderState(chartData));
  useEffect(() => chartDispatch(getChartHeaderState(chartData, currentLayouts)), [IsChartUpdating, Tags, currentLayouts, TagsWithoutData]);
  useEffect(() => {
    if (Duration) runLiveChartTimer(Duration);
  }, [Duration]);

  const tagsWithNoDataDetails = () => {
    const TAG_NAME_MAX_LENGTH = 50;
    const tagItems = TagsWithoutData.map(({ TagName }) => TagName);
    let tagsListNames = tagItems.join(", ");
    if (tagsListNames.length >= TAG_NAME_MAX_LENGTH) {
      tagsListNames = `${tagsListNames.slice(0, TAG_NAME_MAX_LENGTH)}...`;
    }
    return ChartStrings.tagsWithNoData.replace("[%]", tagsListNames);
  };

  const tagsErrorHoverMsg = () => TagsWithoutData.map(({ TagName }) => TagName).join(", ");

  const isFormatBtnActive = plotFormat => Format === CHART_PLOT_FORMATS[plotFormat];
  const isEditBtnActive = type => props.currentEditView === type;
  const handleEditType = e => {
    const editView = e.currentTarget.name;
    if (editView === ChartConstant.CHART_HEADER_ACTIONS.EDIT_TAGS) {
      dispatch(editTagsView({ isOpened: true }));
    } else if (editView === ChartConstant.CHART_HEADER_ACTIONS.EDIT_ALARMS) {
      dispatch(updateAlarms({ isOpened: true }));
    }
    dispatch(setEditCurrentView(editView));
    props.setNormalizedTagConfig(null);
  };
  const onAddNormalizedView = type => props.chartHeaderActionHandler(type);
  const errorNotificationClickHandler = data => chartDispatch({ noTagsDataErrorAlert: data });
  const infoNotificationClickHandler = data => chartDispatch({ chartWarningMessage: data });
  const datePopoverHandler = data => chartDispatch({ popoverAnchor: data });

  const isPlotFormatBtnDisabled = type => {
    let isDisabled = state.isChartPlotTypesDisabled;
    if (!isDisabled) {
      if (type === MULTI_PLOT) {
        isDisabled = state.isMultiplotDisabled;
      } else if (type === MULTI_Y_PLOT) {
        isDisabled = state.isMultiYDisabled;
      }
    }
    return isDisabled;
  };

  const getPlotFormatBtnProps = type => {
    const status = { name: type, tooltip: ChartStrings[type] };
    const isDisabled = isPlotFormatBtnDisabled(type);
    if (isDisabled) {
      status["disabled"] = isDisabled;
    } else {
      status["active"] = isFormatBtnActive(type);
    }
    return status;
  };

  const getPlotFormatButton = type => {
    const btnPorps = getPlotFormatBtnProps(type);
    return (
      <DupontIconButton onClick={handleFormatChange} {...btnPorps}>
        {React.createElement(CHART_HEADER_BUTTONS[type], { ...btnPorps })}
      </DupontIconButton>
    );
  };
  const getBadgeCountAttribute = () => {
    let attr = {};

    const badgeText = Tags?.length;
    if (badgeText !== null) {
      attr = { badgetext: badgeText };
    }
    return attr;
  };

  const getEditButton = type => {
    const iconProps = { name: type, tooltip: ChartStrings[type] };
    if (type === EDIT_NORMALIZE_FILTERS) {
      iconProps.disabled = state.isEditNormalizeDisabled && props.isAddNormalizedTagOpen;
    } else if (type === EDIT_ALARMS) {
      iconProps.disabled = state.isEditAlarmsDisabled;
    }
    return (
      <DupontIconButton {...iconProps} onClick={handleEditType}>
        {type === EDIT_TAGS ? (
          <span className={`badge-icon  badge `} {...getBadgeCountAttribute()}>
            {React.createElement(CHART_HEADER_BUTTONS[type], { ...iconProps, active: isEditBtnActive(type) })}
          </span>
        ) : (
          React.createElement(CHART_HEADER_BUTTONS[type], { ...iconProps, active: isEditBtnActive(type) })
        )}
      </DupontIconButton>
    );
  };

  const handleFormatChange = event => {
    headerActionHandler();
    const actionType = event.currentTarget.name;
    const NewFormat = CHART_PLOT_FORMATS[actionType];
    if (Format !== NewFormat) {
      dispatch(editChartWidget({ Format: NewFormat }));
      props.setChartLayoutSetting({ ...props.chartLayoutSetting, dragmode: false });
    }
  };

  const runLiveChartTimer = value => {
    const isLiveChartView = value === LIVE_CHART;
    props.setIsLiveChart(isLiveChartView);
    ChartUtil.liveChartTimer(isLiveChartView, () => dispatch(refreshPlotData(isLiveChartView)));
  };

  const handleDateRangeChange = range => {
    headerActionHandler();
    if (range && range !== "Custom") {
      const Duration = ChartConstant.CHART_DURATIONS[range];
      dispatch(editChartWidget({ Duration }));
      runLiveChartTimer(range);
    }
  };

  const onCustomRangeSelection = () => chartDispatch({ popoverAnchor: divRef.current });

  const optionClickHandler = event => {
    const clickedOption = event.target.id;
    chartDispatch({ popoverAnchor: null });
    if (clickedOption === "Custom") {
      setTimeout(() => onCustomRangeSelection(), 100); // Added timeout to fix issue when custom was selected and again selecting custom
    }
  };

  const onCustomDateChange = date => {
    const updateData = {
      Duration: ChartConstant.CHART_DURATIONS.Custom,
      StartDate: moment(date.startDate).unix(),
      EndDate: moment(date.endDate).unix()
    };
    dispatch(editChartWidget(updateData));
  };

  const timeRenderOption = option => (
    <div className="fullWidth" onClick={optionClickHandler} id={option}>
      {option}
    </div>
  );

  const headerActionHandler = () => dispatch(setEditCurrentView(CHART_HEADER_ACTIONS.NONE));

  return (
    <div className="chart-header">
      {state.noTagsDataErrorAlert && (
        <ScreenStatusBox type="error" message={tagsWithNoDataDetails()} onClose={errorNotificationClickHandler} tooltip={tagsErrorHoverMsg()} />
      )}
      {state.chartWarningMessage && <ScreenStatusBox type="warning" message={state.chartWarningMessage} onClose={infoNotificationClickHandler} />}
      <div className="d-flex">
        <div id="plot-type-container" className="chart-header__plot-types">
          <label className="dupont-form-label" disabled={state.isChartPlotTypesDisabled}>
            {ChartStrings.plotType}
          </label>
          <div className="d-flex">
            {getPlotFormatButton("SINGLE_PLOT")}
            <span className="leftRightMargin8">{getPlotFormatButton("MULTI_Y_PLOT")}</span>
            {getPlotFormatButton("MULTI_PLOT")}
          </div>
        </div>
        <div id="chart-daterange-container" className="chart-header--drop-down">
          <Dropdown
            dropdownRef={divRef}
            labelText={ChartStrings.dateRange}
            value={Duration}
            options={Object.keys(ChartConstant.CHART_DURATIONS)}
            getOptionLabel={option => option}
            onChange={handleDateRangeChange}
            disabled={state.isPlotRangeDisabled}
            renderOption={timeRenderOption}
            placeholder={DROPDOWN_STRINGS.chooseOptionStr}
          />
          {state.popoverAnchor !== null && (
            <DatePopover
              page="Chart"
              setSelectedStartEndDates={onCustomDateChange}
              popoverAnchor={state.popoverAnchor}
              setPopoverAnchor={datePopoverHandler}
              startDate={DurationRanges.startTime * 1000} // Setting time in milliseconds
              endDate={DurationRanges.endTime * 1000} // Setting time in milliseconds
            />
          )}
        </div>

        <div id="chart-add-tag-container" className="chart-header--drop-down">
          <label className="dupont-form-label" disabled={state.isAddTagsDisabled}>
            {ChartStrings.addChartTags}
          </label>
          <ChartAddTag
            setCreateBaseline={props.setCreateBaseline}
            tagValue={props.tagValue}
            setTagValue={props.setTagValue}
            normalizedTagConfig={props.normalizedTagConfig}
            setNormalizedTagConfig={props.setNormalizedTagConfig}
            chartLayoutID={props.chartLayoutID}
            activeNormalizedTags={props.activeNormalizedTags}
            currentNormalizedTagDialog={props.currentNormalizedTagDialog}
            onAddNormalizedView={onAddNormalizedView}
            onTagSelection={headerActionHandler}
            disabled={state.isAddTagsDisabled}
          />
        </div>

        {state.isEditTagsVisible && (
          <div id="plot-type-container" className="chart-header__plot-types">
            <label className="dupont-form-label">{ChartStrings.editDetails}</label>
            <div className="d-flex">
              {getEditButton(EDIT_TAGS)}
              <span className="alarm-icon-padding">{getEditButton(EDIT_ALARMS)}</span>
              {getEditButton(EDIT_NORMALIZE_FILTERS)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartHeader;
